<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { PlusCircleIcon } from '@heroicons/vue/20/solid';
import { useErhlLoanAppProcess } from '@/helpers/useErhlLoanAppProcess';
import { ApplicantType, BorrowerType } from '@/helpers/const';
import { pendingOwnershipStorage } from '@/helpers/pendingOwnershipStorage';
import {
  createApplicant,
  generateUniqueId,
  getActiveEntityIncome,
  withoutLegacyCompanyIncome,
} from '@/helpers/builder';
import type { ApplicantEntry } from '@/types';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import { BorrowerRole } from '@/types/BorrowerGroup';
import BorrowerCard from './modules/BorrowersDetails/BorrowerCard.vue';
import PrivacyConsentModal from '@/components/shared/PrivacyConsentModal.vue';
import TermsAndAuthorisationsModal from '@/components/shared/TermsAndAuthorisationsModal.vue';
import LogRocket from 'logrocket';
import api from '@/api';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';
import {
  useRelationshipManager,
  transformApiToUiFormat,
  transformRelationshipStatuses,
  prepareFinancials,
  cleanupStalePartnerReferences,
} from '@/helpers/useRelationshipManager';

// Secondary borrower with stable card ID (separate from applicant.id which can change when linking)
interface SecondaryBorrower {
  cardId: string; // Stable ID for Vue's :key - never changes
  applicant: ApplicantEntry;
}

// Auto-add new borrower when arriving from Property Ownership "Add additional borrower" action
onMounted(() => {
  if (pendingOwnershipStorage.exists()) {
    const newApplicant = createApplicant(ApplicantType.SECONDARY);
    const cardId = generateUniqueId('card');
    secondaries.value = [...secondaries.value, { cardId, applicant: newApplicant }];
    pendingOwnershipStorage.remove();
  }
});

const { loanApp, state, saveAndGoNext, goBack } = useErhlLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();
const { getMaxSecondaryApplicants } = useFeatureFlags();
const { flagSessionExpired } = useSessionExpiry();

const savedApplicants = loanApp.value.applicants.length > 0 ? transformApiToUiFormat(loanApp.value.applicants) : [];
const initialBorrowerGroups = loanApp.value.borrowerGroups ?? [];

// Map secondary cardId -> initial borrower group (for data restoration)
const secondaryBorrowerGroupMap = new Map<string, BorrowerGroup>();

// Track applicant IDs removed during this session (for dropdown filtering)
// This is session-only - refresh restores removed applicants from savedApplicants
const removedApplicantIds = ref(new Set<string>());

// Primary borrower group is always the first in the array
const getPrimaryBorrowerGroup = (): BorrowerGroup | undefined => initialBorrowerGroups[0];

// For CRM scenarios, borrowerGroups is absent but the applicant has name data.
// Derive a minimal INDIVIDUAL BorrowerGroup so BorrowerCard auto-selects the correct type.
const deriveInitialBorrowerGroup = (applicant: ApplicantEntry): BorrowerGroup | undefined => {
  if (!applicant.firstName || !applicant.lastName) return undefined;
  return {
    id: `derived-${applicant.id}`,
    type: BorrowerType.INDIVIDUAL,
    applicants: [{ id: `derived-${applicant.id}-member`, applicantId: applicant.id, role: BorrowerRole.INDIVIDUAL }],
  };
};

const getApplicantById = (id: string): ApplicantEntry | undefined => {
  return savedApplicants.find((a) => a.id === id);
};

const initializePrimary = (): ApplicantEntry => {
  const primaryGroup = getPrimaryBorrowerGroup();
  if (primaryGroup?.applicants?.[0]) {
    const applicant = getApplicantById(primaryGroup.applicants[0].applicantId);
    if (applicant) return applicant;
  }
  // Fallback: find primary type applicant or create new
  return savedApplicants.find((a) => a.type === ApplicantType.PRIMARY) ?? createApplicant(ApplicantType.PRIMARY);
};

// Initialize secondary borrowers based on borrower groups (not flat applicant list)
const initializeSecondaries = (primaryId?: string): SecondaryBorrower[] => {
  if (initialBorrowerGroups.length > 1) {
    const [, ...secondaryGroups] = initialBorrowerGroups;
    return secondaryGroups.reduce<SecondaryBorrower[]>((accumulated, group) => {
      const cardId = generateUniqueId('card');
      // Store the borrower group by cardId for data restoration
      secondaryBorrowerGroupMap.set(cardId, group);

      if (group.type === BorrowerType.INDIVIDUAL && group.applicants?.[0]) {
        const applicant = getApplicantById(group.applicants[0].applicantId);
        if (applicant) {
          return [...accumulated, { cardId, applicant }];
        }
      }
      const placeholder = createApplicant(ApplicantType.SECONDARY);
      return [...accumulated, { cardId, applicant: placeholder }];
    }, []);
  }
  // No borrower groups - fall back to old behavior, excluding the applicant already used as primary
  return savedApplicants
    .filter((a) => a.type === ApplicantType.SECONDARY && a.id !== primaryId)
    .map((applicant) => ({ cardId: generateUniqueId('card'), applicant }));
};

const initialPrimary = initializePrimary();
const primary = ref<ApplicantEntry>(initialPrimary);
const secondaries = ref<SecondaryBorrower[]>(initializeSecondaries(initialPrimary.id));

const primaryRef = ref<InstanceType<typeof BorrowerCard>>();
// Use a Map to track refs by cardId - this properly handles removals and applicant ID changes
const secondaryRefsMap = ref<Map<string, InstanceType<typeof BorrowerCard>>>(new Map());

// Setter function for template refs that tracks by cardId (stable, never changes)
const setSecondaryRef = (cardId: string) => (el: unknown) => {
  if (el) {
    secondaryRefsMap.value.set(cardId, el as InstanceType<typeof BorrowerCard>);
  } else {
    secondaryRefsMap.value.delete(cardId);
  }
};

const getActiveSecondaryRefs = (): InstanceType<typeof BorrowerCard>[] => {
  return secondaries.value
    .map((s) => secondaryRefsMap.value.get(s.cardId))
    .filter((ref): ref is InstanceType<typeof BorrowerCard> => !!ref);
};

const isLoading = ref(false);

const allowAdditionalApplicants = computed(() => secondaries.value.length < getMaxSecondaryApplicants());

// allIndividuals: Reactive list of ALL individuals from all BorrowerCards
// Includes directors/trustees from Company/Trust sections for relationship management
const allIndividuals = computed(() => {
  // Get individuals from primary BorrowerCard (could be individual or directors/trustees)
  const primaryIndividuals = primaryRef.value?.individuals ?? [primary.value];

  const secondaryIndividuals = secondaries.value.flatMap((secondary) => {
    const ref = secondaryRefsMap.value.get(secondary.cardId);
    return ref?.individuals ?? [secondary.applicant];
  });

  const combined = [...primaryIndividuals, ...secondaryIndividuals];
  const seenIds = new Set<string>();
  const unique: ApplicantEntry[] = [];
  for (const individual of combined) {
    if (individual && !seenIds.has(individual.id)) {
      seenIds.add(individual.id);
      unique.push(individual);
    }
  }

  // Also include any saved applicants not yet in the list (for initial load)
  // BUT filter out applicants that were removed during this session
  for (const saved of savedApplicants) {
    if (!seenIds.has(saved.id) && !removedApplicantIds.value.has(saved.id)) {
      seenIds.add(saved.id);
      unique.push(saved);
    }
  }

  return unique;
});

const { validate: relationshipValidate, getRequiredChanges } = useRelationshipManager(allIndividuals);

// Compute IDs of applicants already used as Individual borrowers (for deduplication)
const individualBorrowerIds = computed(() => {
  const ids: string[] = [];

  if (primaryRef.value?.selectedBorrowerType === BorrowerType.INDIVIDUAL) {
    if (primary.value.firstName && primary.value.lastName) {
      ids.push(primary.value.id);
    }
  }

  for (const ref of getActiveSecondaryRefs()) {
    if (ref?.selectedBorrowerType === BorrowerType.INDIVIDUAL) {
      const applicants = ref.getApplicants?.() ?? [];
      const savedApplicantIds = applicants.filter((a) => a.firstName && a.lastName).map((a) => a.id);
      ids.push(...savedApplicantIds);
    }
  }

  return ids;
});

// Track whether to show relationship validation errors (after user attempts to submit)
const showRelationshipErrors = ref(false);

// Format relationship errors for display (strip ID prefix for cleaner messages)
const formattedRelationshipErrors = computed(() =>
  relationshipValidate.value.map((error) => error.replace(/^\[[^\]]+\]\s*/, '')),
);

// Find and update an individual's relationship status across all sources
// When primary is Company/Trust, primary.value is a placeholder sharing the same ID as the first
// director/trustee, so we skip it and let the nested individuals search handle the update.
const updateIndividualRelationship = (id: string, status: string) => {
  const primaryIsIndividual = primaryRef.value?.selectedBorrowerType === BorrowerType.INDIVIDUAL;
  if (primaryIsIndividual && primary.value.id === id) {
    primary.value.relationshipStatus = status as ApplicantEntry['relationshipStatus'];
    return true;
  }

  const secondary = secondaries.value.find((s) => s.applicant.id === id);
  if (secondary) {
    secondary.applicant.relationshipStatus = status as ApplicantEntry['relationshipStatus'];
    return true;
  }

  const primaryIndividual = primaryRef.value?.individuals?.find((i: ApplicantEntry) => i.id === id);
  if (primaryIndividual) {
    primaryIndividual.relationshipStatus = status as ApplicantEntry['relationshipStatus'];
    return true;
  }

  for (const ref of getActiveSecondaryRefs()) {
    const individual = ref?.individuals?.find((i: ApplicantEntry) => i.id === id);
    if (individual) {
      individual.relationshipStatus = status as ApplicantEntry['relationshipStatus'];
      return true;
    }
  }

  return false;
};

const handleRelationshipChange = (applicantId: string, newStatus: string, oldStatus?: string) => {
  const changes = getRequiredChanges(applicantId, newStatus, oldStatus);

  for (const change of changes) {
    updateIndividualRelationship(change.id, change.relationshipStatus);
  }

  // Clear error state when user makes changes
  if (showRelationshipErrors.value && relationshipValidate.value.length === 0) {
    showRelationshipErrors.value = false;
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const omitType = ({ type, ...rest }: ApplicantEntry): Omit<ApplicantEntry, 'type'> => rest;

// Preserves the original 'type' field to prevent primary->secondary overwrites when syncing linked applicants
const updateApplicantInArray = (arr: ApplicantEntry[], id: string, data: ApplicantEntry) => {
  const index = arr.findIndex((item) => item.id === id);
  if (index >= 0) {
    arr[index] = { ...arr[index], ...omitType(data) };
  }
};

// Preserves the original 'type' field to prevent primary->secondary overwrites when syncing linked applicants
const updateSecondaryApplicant = (id: string, data: ApplicantEntry) => {
  const secondary = secondaries.value.find((s) => s.applicant.id === id);
  if (secondary) {
    secondary.applicant = { ...secondary.applicant, ...omitType(data) };
  }
};

// Sync applicant data across all references (when a linked applicant is updated)
const handleSyncApplicant = (updatedApplicant: ApplicantEntry) => {
  const { id } = updatedApplicant;

  if (primary.value.id === id) {
    primary.value = { ...primary.value, ...omitType(updatedApplicant) };
  }

  updateSecondaryApplicant(id, updatedApplicant);

  if (primaryRef.value?.individuals) {
    updateApplicantInArray(primaryRef.value.individuals, id, updatedApplicant);
  }

  for (const ref of getActiveSecondaryRefs()) {
    if (ref?.individuals) {
      updateApplicantInArray(ref.individuals, id, updatedApplicant);
    }
  }
};

// Track removed applicant IDs so they don't appear in dropdowns
const handleRemoveApplicant = (applicantId: string) => {
  const next = new Set(removedApplicantIds.value);
  next.add(applicantId);
  removedApplicantIds.value = next;
};

const validateBorrowers = async () => {
  let isExpanded = false; // to expand form one at the time
  const validatePrimary = (await primaryRef.value?.update()) ?? false;
  if (!validatePrimary) {
    isExpanded = true;
    primaryRef.value?.expandAndFocus();
  }
  const activeRefs = getActiveSecondaryRefs();
  const renderedRefs = activeRefs.filter((ref) => ref?.update);
  const validateSecondaries = await Promise.all(renderedRefs.map((ref) => ref.update()));
  const allSecondariesValid = validateSecondaries.every(Boolean);
  for (let i = 0; i < renderedRefs.length; i++) {
    if (validateSecondaries[i] === false && isExpanded === false) {
      isExpanded = true;
      renderedRefs[i]?.expandAndFocus();
    }
  }
  return validatePrimary && allSecondariesValid;
};

// Find and expand the BorrowerCard containing an individual (handles nested directors/trustees)
const expandCardWithIndividual = (individualId: string): boolean => {
  // Check if individual is in primary BorrowerCard (could be the borrower or a nested director/trustee)
  const primaryIndividuals = primaryRef.value?.individuals ?? [primary.value];
  if (primaryIndividuals.some((i: ApplicantEntry) => i.id === individualId)) {
    // Use expandIndividual to drill down to the specific PersonCard
    return primaryRef.value?.expandIndividual(individualId) ?? false;
  }

  for (const [, ref] of secondaryRefsMap.value) {
    const cardIndividuals = ref?.individuals ?? [];
    if (cardIndividuals.some((i: ApplicantEntry) => i.id === individualId)) {
      // Use expandIndividual to drill down to the specific PersonCard
      return ref?.expandIndividual(individualId) ?? false;
    }
  }

  // Fallback: check if it's a secondary borrower directly (individual type)
  const secondary = secondaries.value.find((s) => s.applicant.id === individualId);
  if (secondary) {
    const ref = secondaryRefsMap.value.get(secondary.cardId);
    return ref?.expandIndividual(individualId) ?? false;
  }

  return false;
};

const validateRelations = () => {
  const errors = relationshipValidate.value;
  const hasErrors = errors.length > 0;
  showRelationshipErrors.value = hasErrors;

  if (hasErrors) {
    // Extract applicant ID from first error (format: "[id] message")
    const firstError = errors[0];
    const idMatch = firstError.match(/^\[([^\]]+)\]/);
    if (idMatch) {
      const errorApplicantId = idMatch[1];
      expandCardWithIndividual(errorApplicantId);
    }
  }

  return !hasErrors;
};

const addApplicant = async () => {
  if (await validateBorrowers()) {
    const newApplicant = createApplicant(ApplicantType.SECONDARY);
    const cardId = generateUniqueId('card');
    secondaries.value = [...secondaries.value, { cardId, applicant: newApplicant }];
  }
};

const removeApplicant = (cardId: string) => {
  secondaryRefsMap.value.delete(cardId);
  secondaryBorrowerGroupMap.delete(cardId);
  secondaries.value = secondaries.value.filter((s) => s.cardId !== cardId);
};

const collectBorrowerGroups = (): BorrowerGroup[] => {
  const primaryGroup = primaryRef.value?.getBorrowerGroup?.();
  const secondaryGroups = getActiveSecondaryRefs()
    .map((ref) => ref?.getBorrowerGroup?.())
    .filter((group): group is BorrowerGroup => !!group);

  return primaryGroup ? [primaryGroup, ...secondaryGroups] : secondaryGroups;
};

// Collect all applicants including directors/trustees from Company/Trust borrowers
const collectAllApplicants = (): ApplicantEntry[] => {
  const primaryApplicants = primaryRef.value?.getApplicants?.() ?? [];
  const secondaryApplicants = getActiveSecondaryRefs().flatMap((ref) => ref?.getApplicants?.() ?? []);

  const allApplicants = [...primaryApplicants, ...secondaryApplicants];

  // Deduplicate by applicant ID (data should already be synced via handleSyncApplicant)
  const uniqueMap = new Map<string, ApplicantEntry>();
  for (const applicant of allApplicants) {
    if (!uniqueMap.has(applicant.id)) {
      uniqueMap.set(applicant.id, applicant);
    }
  }

  return Array.from(uniqueMap.values());
};

const saveAndContinue = async () => {
  if (!(await validateBorrowers())) return;
  if (!validateRelations()) return;

  isLoading.value = true;

  const allCollectedApplicants = collectAllApplicants();
  cleanupStalePartnerReferences(allCollectedApplicants);
  const applicants = transformRelationshipStatuses(allCollectedApplicants);
  const financials = withoutLegacyCompanyIncome(prepareFinancials(allCollectedApplicants, loanApp.value.financials));

  const borrowerGroups = collectBorrowerGroups();
  const entityIncome = getActiveEntityIncome(loanApp.value, borrowerGroups);

  const domainUrl = import.meta.env.VITE_APP_CUSTOM_THEME_DOMAIN_URL ?? 'apply.bridgit.com.au';
  if (window.location.host === domainUrl && primary.value.email?.length) {
    LogRocket.identify(primary.value.email, {
      name: `${primary.value.firstName} ${primary.value.lastName}`,
      email: primary.value.email as string,
      mobilePhoneNumber: primary.value.mobile as string,
    });
  }

  let applicationDraftId = loanApp.value.applicationDraftId;
  try {
    if (loanApp.value.brokerId) {
      const savedDraftId = await saveApplicationToDraft(state.value.context, {
        ...loanApp.value,
        applicants,
        financials,
        borrowerGroups,
        entityIncome,
      });
      applicationDraftId = savedDraftId ?? applicationDraftId;
    }
    saveAndGoNext({
      applicants,
      financials,
      borrowerGroups,
      entityIncome,
      applicationDraftId,
    });
  } catch (error) {
    if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
      flagSessionExpired();
      return;
    }
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
};

const showPrivacyModal = ref(false);
const showTermsAndAuthModal = ref(false);
</script>

<template>
  <div class="w-full">
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">
        Provide
        <BxHighlight>applicant details</BxHighlight>
      </h2>
      <p v-if="allowAdditionalApplicants" class="mt-2">
        Enter the details for each applicant. You can add additional applicants using the button below.
      </p>
    </div>

    <div class="mt-6">
      <BorrowerCard
        ref="primaryRef"
        v-model="primary"
        type="primary"
        data-test-id="borrower-primary-card"
        :borrower-index="0"
        :all-individuals="allIndividuals"
        :current-id="primary.id"
        :initial-borrower-group="getPrimaryBorrowerGroup() ?? deriveInitialBorrowerGroup(primary)"
        :force-show-errors="showRelationshipErrors"
        :excluded-individual-borrower-ids="individualBorrowerIds"
        @relationship-change="handleRelationshipChange"
        @sync-applicant="handleSyncApplicant"
        @remove-applicant="handleRemoveApplicant"
      />
    </div>
    <div v-if="secondaries.length > 0" class="mt-6">
      <BorrowerCard
        v-for="(secondary, index) in secondaries"
        :key="secondary.cardId"
        :ref="setSecondaryRef(secondary.cardId)"
        v-model="secondary.applicant"
        type="secondary"
        :data-test-id="`borrower-secondary-card-${secondary.cardId}`"
        :borrower-index="index + 1"
        :primary="primary"
        :all-individuals="allIndividuals"
        :current-id="secondary.applicant.id"
        :initial-borrower-group="secondaryBorrowerGroupMap.get(secondary.cardId)"
        :force-show-errors="showRelationshipErrors"
        :excluded-individual-borrower-ids="individualBorrowerIds"
        @remove="removeApplicant(secondary.cardId)"
        @relationship-change="handleRelationshipChange"
        @sync-applicant="handleSyncApplicant"
        @remove-applicant="handleRemoveApplicant"
      />
    </div>

    <div v-if="allowAdditionalApplicants" class="mt-6 flex">
      <BxButton id="borrower-add" data-test-id="borrower-add" variant="tertiary" @click="addApplicant()">
        <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
        Add another applicant
      </BxButton>
    </div>

    <BxErrorPanel v-if="showRelationshipErrors" class="mt-6">
      <p v-for="error in formattedRelationshipErrors" :key="error" class="mb-1">
        {{ error }}
      </p>
    </BxErrorPanel>
    <p class="mt-10 text-sm">
      By clicking <strong>"Save and continue"</strong> I confirm that the applicant/s has read and agrees to Bridgit's
      <a
        data-test-id="privacy-consent-link"
        class="cursor-pointer text-bridgit-navy underline hover:text-bridgit-vibrantGreen"
        @click="showPrivacyModal = true"
      >
        Privacy Consent and Electronic Authorisation</a
      >
      and that the applicant/s consents to the collection, use, holding and disclosure of their personal information as
      set out in that document, and to receiving notices, statements, disclosures and other documents from Bridgit
      electronically. I also agree to the
      <a
        data-test-id="terms-auth-link"
        class="cursor-pointer text-bridgit-navy underline hover:text-bridgit-vibrantGreen"
        @click="showTermsAndAuthModal = true"
      >
        terms and authorisations
      </a>
      of the aggregator agreement between Bridgit and my aggregator.
    </p>

    <div class="mt-10 flex items-center justify-start">
      <BxButton
        id="borrower-continue"
        data-test-id="borrower-continue"
        variant="primary"
        :loading="isLoading"
        @click="saveAndContinue()"
        >Save and continue</BxButton
      >
      <BxButton
        v-if="state.nextEvents.includes('BACK')"
        data-test-id="borrower-back"
        variant="link"
        class="ml-4"
        @click="goBack()"
        >Back</BxButton
      >
    </div>
    <PrivacyConsentModal v-model="showPrivacyModal" />
    <TermsAndAuthorisationsModal v-model="showTermsAndAuthModal" />
  </div>
</template>
